import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Snackbar,
  LinearProgress,
  Toolbar,
  AppBar,
  TextField,
  Checkbox,
  ListItemText,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import FlagIcon from '@mui/icons-material/Flag';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../services/api';
import { IMAGE_BASE_URLS } from '../utils/imageUrl';
import { getUserData } from '../utils/cookies';
import {
  PageName,
  TaskType,
  TaskDetails,
  ApiResponse,
  User,
  Vehicle,
  Plate,
  Detection,
  DetectionDataset,
  DetectionFlag,
  BaseEntity,
  PlateRegion,
  Model,
  MakeWithModels,
  Color,
  Body,
  VehicleType,
  Flag,
  EditField,
} from '../types';
import {
  DrawerNavigation,
  PaginationBar,
  MMCCard,
  PlateCard,
  DetectionCard,
  EditDialog,
  PlateAlignmentEditor,
  ColorGrid,
  DetectionObjectEditor,
  ConfirmDialog,
} from '../components';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SampleStatusButtons, { SampleStatus } from '../components/SampleStatusButtons';
import { DRAWER_WIDTH, COLLAPSED_WIDTH } from '../constants/ui';
import { TASK_TYPE_CONFIG, getTaskBgColor } from '../constants/taskConfig';

// Constants
const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'noAction', label: 'No Action' },
];
const MAX_FILTER_COLORS = 3;

// Helper function to determine if a color is light (for text contrast)
const isLightColor = (hex?: string): boolean => {
  if (!hex) return true;
  const color = hex.replace('#', '');
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

// Country flag emoji based on region code
const getCountryFlag = (code: string): string => {
  if (code.startsWith('us-')) return '🇺🇸';
  if (code.startsWith('ca-')) return '🇨🇦';
  return '';
};

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface TaskPageProps {
  taskId: string;
  onLogout: () => void;
  onNavigate: (page: PageName, taskId?: string) => void;
}

// Shared overlay for task cards (checkbox + status buttons + hide)
interface TaskCardOverlayProps {
  sampleId: string;
  isSelected: boolean;
  status: SampleStatus;
  onToggleSelection: (id: string) => void;
  onMarkCompleted: (id: string) => void;
  onMarkNoAction: (id: string) => void;
  onUnmark: (id: string, fromStatus: 'completed' | 'noAction') => void;
  onHide: (id: string) => void;
  children: React.ReactNode;
}

const TaskCardOverlay: React.FC<TaskCardOverlayProps> = ({
  sampleId, isSelected, status,
  onToggleSelection, onMarkCompleted, onMarkNoAction, onUnmark, onHide,
  children,
}) => {
  const theme = useTheme();
  const [overlaysHidden, setOverlaysHidden] = useState(false);
  const overlayBg = theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)';
  const overlayHoverBg = theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,1)';

  return (
    <Box
      sx={{ position: 'relative' }}
      onContextMenu={(e) => { e.preventDefault(); setOverlaysHidden(prev => !prev); }}
    >
      {!overlaysHidden && (
        <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10, display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelection(sampleId)}
            size="small"
            sx={{
              bgcolor: overlayBg,
              borderRadius: 1,
              p: 0.5,
              '&:hover': { bgcolor: overlayHoverBg },
            }}
          />
          <Box sx={{ bgcolor: overlayBg, borderRadius: 1, display: 'flex' }}>
            <SampleStatusButtons
              sampleId={sampleId}
              status={status}
              onMarkCompleted={onMarkCompleted}
              onMarkNoAction={onMarkNoAction}
              onUnmark={onUnmark}
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => onHide(sampleId)}
            title="Hide item"
            sx={{
              bgcolor: overlayBg,
              borderRadius: 1,
              p: 0.5,
              '&:hover': { bgcolor: overlayHoverBg },
            }}
          >
            <VisibilityOffIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
      {children}
    </Box>
  );
};

function TaskPage({ taskId, onLogout, onNavigate }: TaskPageProps) {
  const theme = useTheme();
  // UI state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingAllSamples, setLoadingAllSamples] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [allSamplesLoaded, setAllSamplesLoaded] = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [togglingFinished, setTogglingFinished] = useState(false);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  const [overlayDismissed, setOverlayDismissed] = useState(false);

  // User data
  const [user, setUser] = useState<User | null>(null);

  // Task data
  const [task, setTask] = useState<TaskDetails | null>(null);
  const [completedSet, setCompletedSet] = useState<Set<string>>(new Set());
  const [noActionSet, setNoActionSet] = useState<Set<string>>(new Set());

  // Sample data
  const [displayedSamples, setDisplayedSamples] = useState<(Vehicle | Plate | Detection)[]>([]);
  // Map of sample ID -> sample data (for color filtering - accumulates as samples are fetched)
  const [sampleDataMap, setSampleDataMap] = useState<Map<string, Vehicle | Plate | Detection>>(new Map());

  // Pagination and filter
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterOption, setFilterOption] = useState<'all' | 'pending' | 'completed' | 'noAction'>('all');
  const [filteredSampleIds, setFilteredSampleIds] = useState<string[]>([]);
  const [selectedFilterColors, setSelectedFilterColors] = useState<string[]>([]);

  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // Dropdown data for editing (type-specific)
  const [mmcDropdowns, setMmcDropdowns] = useState<{
    makes: MakeWithModels[];
    colors: Color[];
    bodies: Body[];
    types: VehicleType[];
    flags: Flag[];
    datasets: BaseEntity[];
  } | null>(null);

  const [plateDropdowns, setPlateDropdowns] = useState<{
    regions: PlateRegion[];
    datasets: BaseEntity[];
    flags: BaseEntity[];
  } | null>(null);

  const [detectionDropdowns, setDetectionDropdowns] = useState<{
    datasets: DetectionDataset[];
    flags: DetectionFlag[];
  } | null>(null);

  // Bulk edit state
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // MMC bulk edit fields
  const [bulkModel, setBulkModel] = useState<string>('');
  const [bulkBody, setBulkBody] = useState<string>('');
  const [bulkType, setBulkType] = useState<string>('');
  const [bulkColors, setBulkColors] = useState<string[]>([]);
  const [bulkYear, setBulkYear] = useState<string>('');
  const [bulkFlags, setBulkFlags] = useState<string[]>([]);

  // Plate bulk edit fields
  const [bulkTag, setBulkTag] = useState<string>('');
  const [bulkRegion, setBulkRegion] = useState<string>('');
  const [bulkDataset, setBulkDataset] = useState<string>('');
  const [bulkPlateFlags, setBulkPlateFlags] = useState<string[]>([]);

  // Detection bulk edit fields
  const [bulkDetDataset, setBulkDetDataset] = useState<string>('');
  const [bulkDetFlags, setBulkDetFlags] = useState<string[]>([]);

  // MMC Edit Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [editField, setEditField] = useState<EditField | null>(null);
  const [editValue, setEditValue] = useState<any>(null);
  const [editModelValue, setEditModelValue] = useState<string>('');
  const [editSaving, setEditSaving] = useState(false);

  // Plate Alignment Editor state
  const [alignmentEditorOpen, setAlignmentEditorOpen] = useState(false);
  const [editingPlate, setEditingPlate] = useState<Plate | null>(null);

  // Detection Object Editor state
  const [detectionEditorOpen, setDetectionEditorOpen] = useState(false);
  const [editingDetection, setEditingDetection] = useState<Detection | null>(null);

  // Refs
  const hasFetchedInitialData = useRef(false);

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Computed values
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Filter out hidden items
  const visibleSamples = useMemo(() => {
    return displayedSamples.filter(sample => {
      const sampleId = task?.type === 'detection'
        ? (sample as Detection).id
        : (sample as Vehicle | Plate).id;
      return !hiddenIds.has(sampleId);
    });
  }, [displayedSamples, hiddenIds, task?.type]);

  // Apply filter function - called when user clicks Filter button
  const applyFilter = useCallback(() => {
    if (!task?.samples) {
      setFilteredSampleIds([]);
      return;
    }

    let result: string[];
    switch (filterOption) {
      case 'completed':
        result = task.samples.filter((id) => completedSet.has(id));
        break;
      case 'noAction':
        result = task.samples.filter((id) => noActionSet.has(id));
        break;
      case 'pending':
        result = task.samples.filter((id) => !completedSet.has(id) && !noActionSet.has(id));
        break;
      case 'all':
      default:
        result = [...task.samples];
    }

    // Apply color filter for MMC tasks (OR logic - at least one color matches)
    if (task.type === 'mmc' && selectedFilterColors.length > 0) {
      result = result.filter((id) => {
        const sample = sampleDataMap.get(id) as Vehicle | undefined;
        if (!sample?.colors) return false;
        const sampleColorIds = sample.colors.map(c => c.id);
        return selectedFilterColors.some(colorId => sampleColorIds.includes(colorId));
      });
    }

    setFilteredSampleIds(result);
    setHiddenIds(new Set());
    setCurrentPage(1);
    setSelectedIds([]);
  }, [task?.samples, task?.type, filterOption, completedSet, noActionSet, selectedFilterColors, sampleDataMap]);

  const totalPages = Math.ceil(filteredSampleIds.length / pageSize);

  // Get sample IDs for current page
  const currentPageIds = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredSampleIds.slice(startIdx, startIdx + pageSize);
  }, [filteredSampleIds, currentPage, pageSize]);

  // Get task config
  const config = task ? TASK_TYPE_CONFIG[task.type] : null;
  const IconComponent = config?.icon;

  // Progress calculation
  const progress = task
    ? Math.round(((completedSet.size + noActionSet.size) / task.samples.length) * 100)
    : 0;

  // Get sample status
  const getSampleStatus = useCallback((sampleId: string): SampleStatus => {
    if (completedSet.has(sampleId)) return 'completed';
    if (noActionSet.has(sampleId)) return 'noAction';
    return 'pending';
  }, [completedSet, noActionSet]);

  // Fetch task details on mount
  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;

    const fetchTaskDetails = async () => {
      setLoading(true);
      try {
        // Get user from cookies
        const storedUser = getUserData();
        if (storedUser) {
          setUser({
            id: storedUser.id,
            username: storedUser.username,
            roles: storedUser.roles,
          });
        }

        // Fetch task details
        const taskRes = await api.get<ApiResponse<TaskDetails>>(`tasks/details/${taskId}`);
        const taskData = taskRes.data.results as TaskDetails;
        setTask(taskData);
        setCompletedSet(new Set(taskData.completed));
        setNoActionSet(new Set(taskData.noAction));
        // Initialize filtered samples with all samples (default filter is 'all')
        setFilteredSampleIds([...taskData.samples]);

        // Fetch dropdown data based on task type
        await fetchDropdownData(taskData.type);

        // Set loading to false before fetching samples so progress bar shows
        setLoading(false);

        // Fetch all sample details upfront for color filtering
        if (taskData.samples.length > 0) {
          await fetchAllSampleDetails(taskData.samples, taskData.type);
        } else {
          setAllSamplesLoaded(true);
        }
      } catch (err) {
        console.error('Failed to fetch task details:', err);
        setError('Failed to load task. Please try again.');
        setLoading(false);
      }
    };

    fetchTaskDetails();
  }, [taskId]);

  // Fetch dropdown data based on task type
  const fetchDropdownData = async (type: TaskType) => {
    try {
      if (type === 'mmc') {
        const [makesRes, modelsRes, colorsRes, bodiesRes, typesRes, flagsRes, datasetsRes] = await Promise.all([
          api.get<ApiResponse<any[]>>('mmc/list/makes'),
          api.get<ApiResponse<any[]>>('mmc/list/models'),
          api.get<ApiResponse<Color[]>>('mmc/list/colors'),
          api.get<ApiResponse<Body[]>>('mmc/list/bodies'),
          api.get<ApiResponse<VehicleType[]>>('mmc/list/types'),
          api.get<ApiResponse<Flag[]>>('mmc/list/flags'),
          api.get<ApiResponse<BaseEntity[]>>('datasets/list/mmc'),
        ]);

        // Process makes and models into MakeWithModels
        const makes = makesRes.data.results || [];
        const models = modelsRes.data.results || [];

        const makeWithModels: MakeWithModels[] = makes.map((make: any) => ({
          id: make.id,
          label: make.label,
          models: models
            .filter((m: Model) => m.serie?.make?.id === make.id)
            .map((m: Model) => ({ id: m.id, label: m.label, makeId: make.id }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        })).sort((a: MakeWithModels, b: MakeWithModels) => a.label.localeCompare(b.label));

        setMmcDropdowns({
          makes: makeWithModels,
          colors: colorsRes.data.results || [],
          bodies: bodiesRes.data.results || [],
          types: typesRes.data.results || [],
          flags: flagsRes.data.results || [],
          datasets: datasetsRes.data.results || [],
        });
      } else if (type === 'plate') {
        const [regionsRes, datasetsRes, flagsRes] = await Promise.all([
          api.get<ApiResponse<PlateRegion[]>>('plate/list/regions'),
          api.get<ApiResponse<BaseEntity[]>>('datasets/list/plate'),
          api.get<ApiResponse<BaseEntity[]>>('plate/list/flags'),
        ]);

        setPlateDropdowns({
          regions: regionsRes.data.results || [],
          datasets: datasetsRes.data.results || [],
          flags: flagsRes.data.results || [],
        });
      } else if (type === 'detection') {
        const [datasetsRes, flagsRes] = await Promise.all([
          api.get<ApiResponse<any[]>>('datasets/list/detection'),
          api.get<ApiResponse<any[]>>('detection/list/flags'),
        ]);

        // Convert to DetectionDataset/DetectionFlag types
        setDetectionDropdowns({
          datasets: (datasetsRes.data.results || []).map((d: any) => ({
            id: d.id,
            label: d.label,
          })),
          flags: (flagsRes.data.results || []).map((f: any) => ({
            id: f.id,
            label: f.label,
          })),
        });
      }
    } catch (err) {
      console.error('Failed to fetch dropdown data:', err);
    }
  };

  // Fetch ALL sample details upfront for color filtering
  const fetchAllSampleDetails = async (sampleIds: string[], taskType: TaskType) => {
    setLoadingAllSamples(true);
    setLoadingProgress(0);

    const batchSize = 50;
    const batches: string[][] = [];
    for (let i = 0; i < sampleIds.length; i += batchSize) {
      batches.push(sampleIds.slice(i, i + batchSize));
    }

    const allSamples = new Map<string, Vehicle | Plate | Detection>();
    let completed = 0;

    const endpoint = taskType === 'detection' ? 'detection' : taskType;

    for (const batch of batches) {
      const responses = await Promise.all(
        batch.map(id =>
          api.get<ApiResponse<any>>(`${endpoint}/details/${id}`)
            .catch(err => {
              console.error(`Failed to fetch sample ${id}:`, err);
              return null;
            })
        )
      );

      responses.forEach((res, idx) => {
        if (res) {
          const data = res.data.results;
          allSamples.set(batch[idx], data);
        }
      });

      completed += batch.length;
      setLoadingProgress(Math.round((completed / sampleIds.length) * 100));
    }

    setSampleDataMap(allSamples);
    setAllSamplesLoaded(true);
    setLoadingAllSamples(false);
  };

  // Mark task as finished (one-way action, only admin can unfinish)
  const handleMarkFinished = async () => {
    if (!task) return;
    setTogglingFinished(true);
    setFinishConfirmOpen(false);
    try {
      await api.get(`tasks/finished/${taskId}`);
      setTask(prev => prev ? { ...prev, finished: true } : prev);
      setSnackbar({
        open: true,
        message: 'Task marked as finished',
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to mark as finished:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    } finally {
      setTogglingFinished(false);
    }
  };

  // Get samples for current page from pre-loaded sampleDataMap
  useEffect(() => {
    if (!task || !allSamplesLoaded || currentPageIds.length === 0) return;

    const samples = currentPageIds
      .map(id => sampleDataMap.get(id))
      .filter((s): s is Vehicle | Plate | Detection => s !== undefined);

    setDisplayedSamples(samples);
  }, [task, allSamplesLoaded, currentPageIds, sampleDataMap]);

  // Status marking handlers
  const handleMarkCompleted = useCallback(async (sampleId: string) => {
    try {
      // If currently in noAction, remove it first
      if (noActionSet.has(sampleId)) {
        await api.post(`tasks/samples/taskId/${taskId}/field/noAction/action/pull`, {
          samples: [sampleId],
        });
        setNoActionSet(prev => {
          const next = new Set(prev);
          next.delete(sampleId);
          return next;
        });
      }

      // Add to completed
      await api.post(`tasks/samples/taskId/${taskId}/field/completed/action/push`, {
        samples: [sampleId],
      });
      setCompletedSet(prev => new Set(prev).add(sampleId));
      setSnackbar({ open: true, message: 'Marked as completed', severity: 'success' });
    } catch (err) {
      console.error('Failed to mark as completed:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  }, [taskId, noActionSet]);

  const handleMarkNoAction = useCallback(async (sampleId: string) => {
    try {
      // If currently in completed, remove it first
      if (completedSet.has(sampleId)) {
        await api.post(`tasks/samples/taskId/${taskId}/field/completed/action/pull`, {
          samples: [sampleId],
        });
        setCompletedSet(prev => {
          const next = new Set(prev);
          next.delete(sampleId);
          return next;
        });
      }

      // Add to noAction
      await api.post(`tasks/samples/taskId/${taskId}/field/noAction/action/push`, {
        samples: [sampleId],
      });
      setNoActionSet(prev => new Set(prev).add(sampleId));
      setSnackbar({ open: true, message: 'Marked as no action', severity: 'success' });
    } catch (err) {
      console.error('Failed to mark as no action:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  }, [taskId, completedSet]);

  const handleUnmark = useCallback(async (sampleId: string, fromStatus: 'completed' | 'noAction') => {
    try {
      await api.post(`tasks/samples/taskId/${taskId}/field/${fromStatus}/action/pull`, {
        samples: [sampleId],
      });

      if (fromStatus === 'completed') {
        setCompletedSet(prev => {
          const next = new Set(prev);
          next.delete(sampleId);
          return next;
        });
      } else {
        setNoActionSet(prev => {
          const next = new Set(prev);
          next.delete(sampleId);
          return next;
        });
      }
      setSnackbar({ open: true, message: 'Status cleared', severity: 'success' });
    } catch (err) {
      console.error('Failed to unmark:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  }, [taskId]);

  // Bulk status handlers
  const handleBulkMarkCompleted = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setBulkUpdating(true);

    try {
      // Remove from noAction first (for any that are there)
      const idsInNoAction = selectedIds.filter(id => noActionSet.has(id));
      if (idsInNoAction.length > 0) {
        await api.post(`tasks/samples/taskId/${taskId}/field/noAction/action/pull`, {
          samples: idsInNoAction,
        });
        setNoActionSet(prev => {
          const next = new Set(prev);
          idsInNoAction.forEach(id => next.delete(id));
          return next;
        });
      }

      // Add all to completed
      await api.post(`tasks/samples/taskId/${taskId}/field/completed/action/push`, {
        samples: selectedIds,
      });
      setCompletedSet(prev => {
        const next = new Set(prev);
        selectedIds.forEach(id => next.add(id));
        return next;
      });

      setSnackbar({ open: true, message: `Marked ${selectedIds.length} as completed`, severity: 'success' });
    } catch (err) {
      console.error('Failed to bulk mark as completed:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedIds, taskId, noActionSet]);

  const handleBulkMarkNoAction = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setBulkUpdating(true);

    try {
      // Remove from completed first (for any that are there)
      const idsInCompleted = selectedIds.filter(id => completedSet.has(id));
      if (idsInCompleted.length > 0) {
        await api.post(`tasks/samples/taskId/${taskId}/field/completed/action/pull`, {
          samples: idsInCompleted,
        });
        setCompletedSet(prev => {
          const next = new Set(prev);
          idsInCompleted.forEach(id => next.delete(id));
          return next;
        });
      }

      // Add all to noAction
      await api.post(`tasks/samples/taskId/${taskId}/field/noAction/action/push`, {
        samples: selectedIds,
      });
      setNoActionSet(prev => {
        const next = new Set(prev);
        selectedIds.forEach(id => next.add(id));
        return next;
      });

      setSnackbar({ open: true, message: `Marked ${selectedIds.length} as no action`, severity: 'success' });
    } catch (err) {
      console.error('Failed to bulk mark as no action:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedIds, taskId, completedSet]);

  const handleBulkClearStatus = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setBulkUpdating(true);

    try {
      const idsInCompleted = selectedIds.filter(id => completedSet.has(id));
      const idsInNoAction = selectedIds.filter(id => noActionSet.has(id));

      if (idsInCompleted.length > 0) {
        await api.post(`tasks/samples/taskId/${taskId}/field/completed/action/pull`, {
          samples: idsInCompleted,
        });
        setCompletedSet(prev => {
          const next = new Set(prev);
          idsInCompleted.forEach(id => next.delete(id));
          return next;
        });
      }

      if (idsInNoAction.length > 0) {
        await api.post(`tasks/samples/taskId/${taskId}/field/noAction/action/pull`, {
          samples: idsInNoAction,
        });
        setNoActionSet(prev => {
          const next = new Set(prev);
          idsInNoAction.forEach(id => next.delete(id));
          return next;
        });
      }

      setSnackbar({ open: true, message: `Cleared status for ${selectedIds.length} samples`, severity: 'success' });
    } catch (err) {
      console.error('Failed to clear status:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedIds, taskId, completedSet, noActionSet]);

  // Selection handlers
  const handleToggleSelection = useCallback((id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);

  const handleHideItem = useCallback((id: string) => {
    setHiddenIds(prev => new Set(prev).add(id));
  }, []);

  const handleMmcBoxUpdate = useCallback(async (vehicleId: string, newBoxArray: number[] | null) => {
    try {
      await api.patch(`mmc/patch/${vehicleId}`, { box: newBoxArray });
      const updatedBox = newBoxArray || undefined;
      setDisplayedSamples(prev =>
        prev.map(s => (s as Vehicle).id === vehicleId ? { ...s, box: updatedBox } : s)
      );
      setSampleDataMap(prev => {
        const existing = prev.get(vehicleId);
        if (!existing) return prev;
        const newMap = new Map(prev);
        newMap.set(vehicleId, { ...existing, box: updatedBox } as Vehicle);
        return newMap;
      });
    } catch (err) {
      console.error('Failed to update box:', err);
    }
  }, []);

  const handleSelectAll = useCallback(() => {
    // Select all visible on current page
    const pageIds = visibleSamples.map(s =>
      task?.type === 'detection' ? (s as Detection).id : (s as Vehicle | Plate).id
    );
    setSelectedIds(pageIds);
  }, [visibleSamples, task]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setHiddenIds(new Set());
      setCurrentPage(currentPage - 1);
      setSelectedIds([]);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setHiddenIds(new Set());
      setCurrentPage(currentPage + 1);
      setSelectedIds([]);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = useCallback((page: number) => {
    setHiddenIds(new Set());
    setCurrentPage(page);
    setSelectedIds([]);
  }, []);

  // Sample field change handlers (for inline editing)
  const handlePlateFieldChange = useCallback(async (
    plateId: string,
    field: 'region' | 'dataset' | 'flags',
    value: string | string[]
  ) => {
    try {
      const patch: Record<string, unknown> = {};
      if (field === 'region' || field === 'dataset') {
        patch[field] = { id: value as string };
      } else if (field === 'flags') {
        patch[field] = (value as string[]).map(id => ({ id }));
      }

      await api.patch(`plate/patch/${plateId}`, patch);

      const updatePlate = (plate: Plate): Plate => {
        const updated = { ...plate };
        if (field === 'region' && plateDropdowns) {
          const newRegion = plateDropdowns.regions.find(r => r.id === value);
          if (newRegion) updated.region = newRegion;
        } else if (field === 'dataset' && plateDropdowns) {
          const newDataset = plateDropdowns.datasets.find(d => d.id === value);
          if (newDataset) updated.dataset = newDataset;
        } else if (field === 'flags' && plateDropdowns && Array.isArray(value)) {
          updated.flags = value.map(id => {
            const flag = plateDropdowns.flags.find(f => f.id === id);
            return flag || { id: id, label: id };
          });
        }
        return updated;
      };

      setDisplayedSamples(prev =>
        prev.map(sample =>
          (sample as Plate).id !== plateId ? sample : updatePlate(sample as Plate)
        )
      );

      setSampleDataMap(prev => {
        const existing = prev.get(plateId);
        if (!existing) return prev;
        const newMap = new Map(prev);
        newMap.set(plateId, updatePlate(existing as Plate));
        return newMap;
      });

      setSnackbar({ open: true, message: 'Updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to update plate:', err);
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  }, [plateDropdowns]);

  const handlePlateTagChange = useCallback(async (plateId: string, newTag: string) => {
    const validChars = /^[A-Z0-9]*$/;
    const upperTag = newTag.toUpperCase();
    if (!validChars.test(upperTag)) return;

    try {
      await api.patch(`plate/patch/${plateId}`, { tag: upperTag });
      setDisplayedSamples(prev =>
        prev.map(s => (s as Plate).id === plateId ? { ...s, tag: upperTag } : s)
      );
      setSampleDataMap(prev => {
        const existing = prev.get(plateId);
        if (!existing) return prev;
        const newMap = new Map(prev);
        newMap.set(plateId, { ...existing, tag: upperTag } as Plate);
        return newMap;
      });
    } catch (err) {
      console.error('Failed to update tag:', err);
    }
  }, []);

  const handleDetectionFieldChange = useCallback(async (
    detectionId: string,
    field: 'dataset' | 'flags',
    value: string | string[]
  ) => {
    try {
      const patch: Record<string, unknown> = {};
      if (field === 'dataset') {
        patch[field] = { id: value as string };
      } else if (field === 'flags') {
        patch[field] = (value as string[]).map(id => ({ id }));
      }

      await api.patch(`detection/patch/${detectionId}`, patch);

      const updateSample = (sample: Detection): Detection => {
        const updated = { ...sample };
        if (field === 'dataset' && detectionDropdowns) {
          const newDataset = detectionDropdowns.datasets.find(d => d.id === value);
          if (newDataset) updated.dataset = { id: newDataset.id, label: newDataset.label };
        } else if (field === 'flags' && detectionDropdowns && Array.isArray(value)) {
          updated.flags = value.map(id => {
            const flag = detectionDropdowns.flags.find(f => f.id === id);
            return flag ? { id: flag.id, label: flag.label } : { id, label: id };
          });
        }
        return updated;
      };

      setDisplayedSamples(prev =>
        prev.map(sample =>
          (sample as Detection).id !== detectionId ? sample : updateSample(sample as Detection)
        )
      );

      setSampleDataMap(prev => {
        const existing = prev.get(detectionId);
        if (!existing) return prev;
        const newMap = new Map(prev);
        newMap.set(detectionId, updateSample(existing as Detection));
        return newMap;
      });

      setSnackbar({ open: true, message: 'Updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to update detection:', err);
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  }, [detectionDropdowns]);

  // MMC Edit Dialog handlers
  const openEditDialog = useCallback((vehicle: Vehicle, field: EditField) => {
    setEditingVehicle(vehicle);
    setEditField(field);
    setEditModelValue('');

    switch (field) {
      case 'make':
        setEditValue(vehicle.model?.serie?.make?.id || '');
        setEditModelValue(vehicle.model?.id || '');
        break;
      case 'model':
        setEditValue(vehicle.model?.serie?.make?.id || '');
        setEditModelValue(vehicle.model?.id || '');
        break;
      case 'colors':
        setEditValue(vehicle.colors?.map(c => c.id) || []);
        break;
      case 'body':
        setEditValue(vehicle.body?.id || '');
        break;
      case 'type':
        setEditValue(vehicle.type?.id || '');
        break;
      case 'year':
        setEditValue(vehicle.year || '');
        break;
      case 'flags':
        setEditValue(vehicle.flags?.map(f => f.id) || []);
        break;
      case 'dataset':
        setEditValue(vehicle.dataset?.id || '');
        break;
      default:
        setEditValue(null);
    }
    setEditDialogOpen(true);
  }, []);

  const handleEditDialogSave = useCallback(async () => {
    if (!editingVehicle || !editField || !mmcDropdowns) return;

    setEditSaving(true);
    try {
      const patch: Record<string, unknown> = {};

      switch (editField) {
        case 'make':
        case 'model':
          if (editModelValue) patch.model = { id: editModelValue };
          break;
        case 'colors':
          if (Array.isArray(editValue)) patch.colors = editValue.map(id => ({ id }));
          break;
        case 'body':
          patch.body = editValue ? { id: editValue } : null;
          break;
        case 'type':
          patch.type = editValue ? { id: editValue } : null;
          break;
        case 'year':
          patch.year = editValue ? parseInt(String(editValue), 10) : null;
          break;
        case 'flags':
          if (Array.isArray(editValue)) patch.flags = editValue.map(id => ({ id }));
          break;
        case 'dataset':
          if (editValue) patch.dataset = { id: editValue };
          break;
      }

      if (Object.keys(patch).length > 0) {
        await api.patch(`mmc/patch/${editingVehicle.id}`, patch);

        // Build updated vehicle
        const v = editingVehicle;
        const updated = { ...v };

        if (editField === 'make' || editField === 'model') {
          const make = mmcDropdowns.makes.find(m => m.id === editValue);
          const flatModel = make?.models.find(m => m.id === editModelValue);
          if (flatModel && make) {
            updated.model = {
              id: flatModel.id,
              label: flatModel.label,
              serie: { id: '', label: '', make: { id: make.id, label: make.label } }
            };
          }
        } else if (editField === 'colors' && Array.isArray(editValue)) {
          updated.colors = editValue.map(id => mmcDropdowns.colors.find(c => c.id === id)).filter(Boolean) as Color[];
        } else if (editField === 'body') {
          updated.body = mmcDropdowns.bodies.find(b => b.id === editValue) || null;
        } else if (editField === 'type') {
          updated.type = mmcDropdowns.types.find(t => t.id === editValue) || null;
        } else if (editField === 'year') {
          updated.year = parseInt(String(editValue), 10) || null;
        } else if (editField === 'flags' && Array.isArray(editValue)) {
          updated.flags = editValue.map(id => mmcDropdowns.flags.find(f => f.id === id)).filter(Boolean) as Flag[];
        } else if (editField === 'dataset') {
          updated.dataset = mmcDropdowns.datasets.find(d => d.id === editValue) || null;
        }

        // Update displayedSamples
        setDisplayedSamples(prev =>
          prev.map(s => (s as Vehicle).id !== editingVehicle.id ? s : updated)
        );

        // Update sampleDataMap for color filtering
        setSampleDataMap(prev => {
          const newMap = new Map(prev);
          newMap.set(editingVehicle.id, updated);
          return newMap;
        });

        setSnackbar({ open: true, message: 'Updated successfully', severity: 'success' });
      }

      setEditDialogOpen(false);
    } catch (err) {
      console.error('Failed to save:', err);
      setSnackbar({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setEditSaving(false);
    }
  }, [editingVehicle, editField, editValue, editModelValue, mmcDropdowns]);

  // Plate Alignment Editor handlers
  const handlePlateImageClick = useCallback((plate: Plate) => {
    setEditingPlate(plate);
    setAlignmentEditorOpen(true);
  }, []);

  const handleCornersUpdate = useCallback(async (plateId: string, newCorners: number[][]) => {
    try {
      await api.patch(`plate/patch/${plateId}`, { corners: newCorners });
      setDisplayedSamples(prev =>
        prev.map(s => (s as Plate).id === plateId ? { ...s, corners: newCorners } : s)
      );
      setSampleDataMap(prev => {
        const existing = prev.get(plateId);
        if (!existing) return prev;
        const newMap = new Map(prev);
        newMap.set(plateId, { ...existing, corners: newCorners } as Plate);
        return newMap;
      });
      setSnackbar({ open: true, message: 'Corners updated', severity: 'success' });
    } catch (err) {
      console.error('Failed to update corners:', err);
      setSnackbar({ open: true, message: 'Failed to update corners', severity: 'error' });
    }
  }, []);

  // Detection Object Editor handlers
  const handleDetectionImageClick = useCallback((detection: Detection) => {
    setEditingDetection(detection);
    setDetectionEditorOpen(true);
  }, []);

  const handleDetectionObjectsUpdated = useCallback((detectionId: string, objects: import('../types').DetectionObject[]) => {
    setDisplayedSamples(prev =>
      prev.map(s => (s as Detection).id === detectionId ? { ...s, objects } : s)
    );
    setSampleDataMap(prev => {
      const existing = prev.get(detectionId);
      if (!existing) return prev;
      const newMap = new Map(prev);
      newMap.set(detectionId, { ...existing, objects } as Detection);
      return newMap;
    });
    setEditingDetection(prev =>
      prev && prev.id === detectionId ? { ...prev, objects } : prev
    );
  }, []);

  // Bulk field editing
  const handleBulkApplyFields = useCallback(async () => {
    if (selectedIds.length === 0 || !task) return;
    setBulkUpdating(true);

    try {
      if (task.type === 'mmc') {
        const patch: Record<string, unknown> = {};
        if (bulkModel) patch.model = { id: bulkModel };
        if (bulkBody) patch.body = { id: bulkBody };
        if (bulkType) patch.type = { id: bulkType };
        if (bulkColors.length > 0) patch.colors = bulkColors.map(id => ({ id }));
        if (bulkYear) patch.year = parseInt(bulkYear, 10);
        if (bulkFlags.length > 0) patch.flags = bulkFlags.map(id => ({ id }));

        if (Object.keys(patch).length === 0) {
          setSnackbar({ open: true, message: 'No fields to update', severity: 'warning' });
          setBulkUpdating(false);
          return;
        }

        await Promise.allSettled(
          selectedIds.map(id => api.patch(`mmc/patch/${id}`, patch))
        );
      } else if (task.type === 'plate') {
        const patch: Record<string, unknown> = {};
        if (bulkTag) patch.tag = bulkTag;
        if (bulkRegion) patch.region = { id: bulkRegion };
        if (bulkDataset) patch.dataset = { id: bulkDataset };
        if (bulkPlateFlags.length > 0) patch.flags = bulkPlateFlags.map(id => ({ id }));

        if (Object.keys(patch).length === 0) {
          setSnackbar({ open: true, message: 'No fields to update', severity: 'warning' });
          setBulkUpdating(false);
          return;
        }

        await Promise.allSettled(
          selectedIds.map(id => api.patch(`plate/patch/${id}`, patch))
        );
      } else if (task.type === 'detection') {
        const patch: Record<string, unknown> = {};
        if (bulkDetDataset) patch.dataset = { id: bulkDetDataset };
        if (bulkDetFlags.length > 0) patch.flags = bulkDetFlags.map(id => ({ id }));

        if (Object.keys(patch).length === 0) {
          setSnackbar({ open: true, message: 'No fields to update', severity: 'warning' });
          setBulkUpdating(false);
          return;
        }

        await Promise.allSettled(
          selectedIds.map(id => api.patch(`detection/patch/${id}`, patch))
        );
      }

      setSnackbar({ open: true, message: `Updated ${selectedIds.length} samples`, severity: 'success' });

      // Update local state in place
      const selectedSet = new Set(selectedIds);
      setDisplayedSamples(prev => prev.map(sample => {
        const sampleId = task.type === 'detection' ? (sample as Detection).id : (sample as Vehicle | Plate).id;
        if (!selectedSet.has(sampleId)) return sample;

        if (task.type === 'mmc' && mmcDropdowns) {
          const v = sample as Vehicle;
          const updated = { ...v };
          if (bulkModel) {
            const make = mmcDropdowns.makes.find(m => m.models.some(mod => mod.id === bulkModel));
            const flatModel = make?.models.find(m => m.id === bulkModel);
            if (flatModel && make) {
              updated.model = {
                id: flatModel.id,
                label: flatModel.label,
                serie: { id: '', label: '', make: { id: make.id, label: make.label } }
              };
            }
          }
          if (bulkBody) updated.body = mmcDropdowns.bodies.find(b => b.id === bulkBody) || v.body;
          if (bulkType) updated.type = mmcDropdowns.types.find(t => t.id === bulkType) || v.type;
          if (bulkColors.length > 0) updated.colors = bulkColors.map(id => mmcDropdowns.colors.find(c => c.id === id)).filter(Boolean) as Color[];
          if (bulkYear) updated.year = parseInt(bulkYear, 10);
          if (bulkFlags.length > 0) updated.flags = bulkFlags.map(id => mmcDropdowns.flags.find(f => f.id === id)).filter(Boolean) as Flag[];
          return updated;
        } else if (task.type === 'plate' && plateDropdowns) {
          const p = sample as Plate;
          const updated = { ...p };
          if (bulkTag) updated.tag = bulkTag;
          if (bulkRegion) updated.region = plateDropdowns.regions.find(r => r.id === bulkRegion) || p.region;
          if (bulkDataset) {
            const ds = plateDropdowns.datasets.find(d => d.id === bulkDataset);
            if (ds) updated.dataset = ds;
          }
          if (bulkPlateFlags.length > 0) updated.flags = bulkPlateFlags.map(id => plateDropdowns.flags.find(f => f.id === id)).filter(Boolean) as BaseEntity[];
          return updated;
        } else if (task.type === 'detection' && detectionDropdowns) {
          const d = sample as Detection;
          const updated = { ...d };
          if (bulkDetDataset) {
            const ds = detectionDropdowns.datasets.find(dat => dat.id === bulkDetDataset);
            if (ds) updated.dataset = ds;
          }
          if (bulkDetFlags.length > 0) updated.flags = bulkDetFlags.map(id => detectionDropdowns.flags.find(f => f.id === id)).filter(Boolean) as DetectionFlag[];
          return updated;
        }
        return sample;
      }));

      // Also update sampleDataMap for MMC samples (for color filtering)
      if (task.type === 'mmc' && mmcDropdowns) {
        setSampleDataMap(prev => {
          const newMap = new Map(prev);
          selectedIds.forEach(id => {
            const existing = newMap.get(id) as Vehicle | undefined;
            if (existing) {
              const updated = { ...existing };
              if (bulkModel) {
                const make = mmcDropdowns.makes.find(m => m.models.some(mod => mod.id === bulkModel));
                const flatModel = make?.models.find(m => m.id === bulkModel);
                if (flatModel && make) {
                  updated.model = {
                    id: flatModel.id,
                    label: flatModel.label,
                    serie: { id: '', label: '', make: { id: make.id, label: make.label } }
                  };
                }
              }
              if (bulkBody) updated.body = mmcDropdowns.bodies.find(b => b.id === bulkBody) || existing.body;
              if (bulkType) updated.type = mmcDropdowns.types.find(t => t.id === bulkType) || existing.type;
              if (bulkColors.length > 0) updated.colors = bulkColors.map(cid => mmcDropdowns.colors.find(c => c.id === cid)).filter(Boolean) as Color[];
              if (bulkYear) updated.year = parseInt(bulkYear, 10);
              if (bulkFlags.length > 0) updated.flags = bulkFlags.map(fid => mmcDropdowns.flags.find(f => f.id === fid)).filter(Boolean) as Flag[];
              newMap.set(id, updated);
            }
          });
          return newMap;
        });
      }

      // Clear bulk fields
      setBulkModel('');
      setBulkBody('');
      setBulkType('');
      setBulkColors([]);
      setBulkYear('');
      setBulkFlags([]);
      setBulkTag('');
      setBulkRegion('');
      setBulkDataset('');
      setBulkPlateFlags([]);
      setBulkDetDataset('');
      setBulkDetFlags([]);
    } catch (err) {
      console.error('Failed to bulk update fields:', err);
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedIds, task, bulkModel, bulkBody, bulkType, bulkColors, bulkYear, bulkFlags, bulkTag, bulkRegion, bulkDataset, bulkPlateFlags, bulkDetDataset, bulkDetFlags, mmcDropdowns, plateDropdowns, detectionDropdowns]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  // Render sample card based on task type
  const renderSampleCard = (sample: Vehicle | Plate | Detection) => {
    const sampleId = task?.type === 'detection'
      ? (sample as Detection).id
      : (sample as Vehicle | Plate).id;
    const status = getSampleStatus(sampleId);
    const isSelected = selectedIdsSet.has(sampleId);

    const vehicle = task?.type === 'mmc' ? sample as Vehicle : null;

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={sampleId}>
        <Paper
          elevation={isSelected ? 4 : 1}
          sx={{
            position: 'relative',
            bgcolor: isSelected ? (task ? getTaskBgColor(task.type, theme.palette.mode) : 'transparent') : 'transparent',
            border: 1,
            borderColor: status === 'completed'
              ? '#4caf50'
              : status === 'noAction'
                ? '#ff9800'
                : isSelected
                  ? config?.color
                  : 'divider',
            borderWidth: (status === 'completed' || status === 'noAction' || isSelected) ? 2 : 1,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Sample content based on type */}
          {task?.type === 'mmc' && vehicle && (
            <TaskCardOverlay
              sampleId={sampleId} isSelected={isSelected} status={status}
              onToggleSelection={handleToggleSelection} onMarkCompleted={handleMarkCompleted}
              onMarkNoAction={handleMarkNoAction} onUnmark={handleUnmark} onHide={handleHideItem}
            >
              <MMCCard
                vehicle={vehicle}
                imageBaseUrl={IMAGE_BASE_URLS.mmc}
                isSelected={false}
                onToggleSelection={() => {}}
                onBoxUpdate={handleMmcBoxUpdate}
                onEditField={openEditDialog}
                hideSelection
                onVinClick={user?.roles?.includes('admin') ? (vin) => {
                  window.open(`/?p=searchMmc&vin=${encodeURIComponent(vin)}&pageSize=200`, '_blank');
                } : undefined}
              />
            </TaskCardOverlay>
          )}

          {task?.type === 'plate' && plateDropdowns && (
            <TaskCardOverlay
              sampleId={sampleId} isSelected={isSelected} status={status}
              onToggleSelection={handleToggleSelection} onMarkCompleted={handleMarkCompleted}
              onMarkNoAction={handleMarkNoAction} onUnmark={handleUnmark} onHide={handleHideItem}
            >
              <PlateCard
                plate={sample as Plate}
                imageBaseUrl={IMAGE_BASE_URLS.plate}
                isSelected={false}
                editingTag={(sample as Plate).tag}
                regions={plateDropdowns.regions}
                datasets={plateDropdowns.datasets}
                flags={plateDropdowns.flags}
                onToggleSelection={() => {}}
                onTagChange={handlePlateTagChange}
                onFieldChange={handlePlateFieldChange}
                onImageClick={handlePlateImageClick}
                hideSelection
              />
            </TaskCardOverlay>
          )}

          {task?.type === 'detection' && detectionDropdowns && (
            <TaskCardOverlay
              sampleId={sampleId} isSelected={isSelected} status={status}
              onToggleSelection={handleToggleSelection} onMarkCompleted={handleMarkCompleted}
              onMarkNoAction={handleMarkNoAction} onUnmark={handleUnmark} onHide={handleHideItem}
            >
              <DetectionCard
                detection={sample as Detection}
                imageBaseUrl={IMAGE_BASE_URLS.detection}
                imageWidth={280}
                imageHeight={210}
                isSelected={false}
                datasets={detectionDropdowns.datasets}
                flags={detectionDropdowns.flags}
                onToggleSelection={() => {}}
                onImageClick={handleDetectionImageClick}
                onFieldChange={handleDetectionFieldChange}
                hideSelection
              />
            </TaskCardOverlay>
          )}
        </Paper>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (loadingAllSamples && task) {
    const loadedCount = Math.round((loadingProgress / 100) * task.samples.length);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {config && IconComponent && <IconComponent size={40} />}
          <Typography variant="h5">{task.title}</Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Loading samples...
        </Typography>
        <Box sx={{ width: 400, maxWidth: '80vw' }}>
          <LinearProgress
            variant="determinate"
            value={loadingProgress}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: task ? getTaskBgColor(task.type, theme.palette.mode) : 'action.disabledBackground',
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                bgcolor: config?.color || '#1976d2',
              },
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {loadingProgress}% ({loadedCount} / {task.samples.length})
        </Typography>
      </Box>
    );
  }

  if (!task) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Alert severity="error">Task not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Finished Overlay */}
      {task?.finished && !overlayDismissed && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1050,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 80, mb: 2, color: '#4caf50' }} />
          <Typography variant="h4" sx={{ mb: 1 }}>
            Task Finished
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
            This task is complete. Contact an admin to reopen if needed.
          </Typography>
          {user?.roles?.includes('admin') && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOverlayDismissed(true)}
            >
              Continue Anyway (Admin)
            </Button>
          )}
        </Box>
      )}

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: config?.color,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={() => onNavigate('dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            {IconComponent && <IconComponent size={28} />}
            <Typography variant="h6" noWrap>
              {task.title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mr: 2 }}>
                <Typography variant="body2">@{user.username}</Typography>
              </Box>
            )}
            {!task?.finished && (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<RadioButtonUncheckedIcon />}
                onClick={() => setFinishConfirmOpen(true)}
                disabled={togglingFinished || !task}
              >
                Mark Finished
              </Button>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <DrawerNavigation
        currentPage="taskPage"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: 'background.default',
          transition: 'width 0.2s, margin-left 0.2s',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" disableGutters>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Task Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: task ? getTaskBgColor(task.type, theme.palette.mode) : undefined,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {IconComponent && <IconComponent size={40} />}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5">{task.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {task.description}
                </Typography>
              </Box>
              <Chip
                label={config?.label}
                sx={{
                  bgcolor: config?.color,
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            </Box>

            {/* Progress bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Progress: {completedSet.size + noActionSet.size} / {task.samples.length} samples
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: config?.color }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: task ? getTaskBgColor(task.type, theme.palette.mode) : undefined,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: config?.color,
                  },
                }}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#4caf50' }}>
                  <CheckCircleIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  {completedSet.size} completed
                </Typography>
                <Typography variant="caption" sx={{ color: '#ff9800' }}>
                  <FlagIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  {noActionSet.size} no action
                </Typography>
              </Box>
            </Box>

            {/* Controls */}
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(e.target.value as number);
                      setCurrentPage(1);
                      setSelectedIds([]);
                    }}
                    label="Page Size"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size="grow" />
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter</InputLabel>
                  <Select
                    value={filterOption}
                    onChange={(e) => setFilterOption(e.target.value as typeof filterOption)}
                    label="Filter"
                  >
                    {FILTER_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Color filter for MMC tasks */}
              {task?.type === 'mmc' && mmcDropdowns?.colors && (
                <Grid size={{ xs: 12 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Colors (max {MAX_FILTER_COLORS}){selectedFilterColors.length > 0 && ` - ${selectedFilterColors.length} selected`}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, maxHeight: 100, overflow: 'auto' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {[...mmcDropdowns.colors].sort((a, b) => a.label.localeCompare(b.label)).map((color) => {
                          const isSelected = selectedFilterColors.includes(color.id);
                          const isDisabled = selectedFilterColors.length >= MAX_FILTER_COLORS && !isSelected;
                          const textColor = isLightColor(color.hex) ? '#000' : '#fff';
                          return (
                            <Box
                              key={color.id}
                              onClick={() => {
                                if (isDisabled) return;
                                setSelectedFilterColors(prev =>
                                  isSelected ? prev.filter(c => c !== color.id) : [...prev, color.id]
                                );
                              }}
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: color.hex || '#ccc',
                                borderRadius: 0.5,
                                border: isSelected ? 2 : 1, borderColor: isSelected ? 'primary.main' : 'divider',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDisabled ? 0.4 : 1,
                                transition: 'all 0.1s',
                                '&:hover': !isDisabled ? { transform: 'scale(1.1)', zIndex: 1 } : {},
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title={color.label}
                            >
                              {isSelected && (
                                <Box sx={{ color: textColor, fontSize: 14, fontWeight: 'bold' }}>✓</Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={applyFilter}
                  sx={{ height: 40 }}
                >
                  Apply Filter
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Bulk Actions Panel */}
          {displayedSamples.length > 0 && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: task ? getTaskBgColor(task.type, theme.palette.mode) : undefined }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">
                  Bulk Actions ({selectedIds.length} selected)
                </Typography>
                <Button size="small" variant="outlined" onClick={handleSelectAll}>
                  Select Page
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClearSelection}
                  disabled={selectedIds.length === 0}
                >
                  Clear Selection
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleBulkMarkCompleted}
                  disabled={selectedIds.length === 0 || bulkUpdating}
                >
                  Mark Completed
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<FlagIcon />}
                  onClick={handleBulkMarkNoAction}
                  disabled={selectedIds.length === 0 || bulkUpdating}
                >
                  Mark No Action
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleBulkClearStatus}
                  disabled={selectedIds.length === 0 || bulkUpdating}
                >
                  Clear Status
                </Button>
              </Box>

              {/* Bulk Field Editing */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Bulk Field Editing
              </Typography>
              <Grid container spacing={2} alignItems="flex-end">
                {task.type === 'mmc' && mmcDropdowns && (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Model</InputLabel>
                        <Select
                          value={bulkModel}
                          onChange={(e) => setBulkModel(e.target.value)}
                          label="Set Model"
                        >
                          <MenuItem value=""><em>-- No change --</em></MenuItem>
                          {mmcDropdowns.makes.flatMap((make) =>
                            make.models.map((model) => (
                              <MenuItem key={model.id} value={model.id}>
                                {make.label} - {model.label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Body</InputLabel>
                        <Select
                          value={bulkBody}
                          onChange={(e) => setBulkBody(e.target.value)}
                          label="Set Body"
                        >
                          <MenuItem value=""><em>-- No change --</em></MenuItem>
                          {mmcDropdowns.bodies.map((body) => (
                            <MenuItem key={body.id} value={body.id}>{body.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Type</InputLabel>
                        <Select
                          value={bulkType}
                          onChange={(e) => setBulkType(e.target.value)}
                          label="Set Type"
                        >
                          <MenuItem value=""><em>-- No change --</em></MenuItem>
                          {mmcDropdowns.types.map((type) => (
                            <MenuItem key={type.id} value={type.id}>{type.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Year"
                        type="number"
                        value={bulkYear}
                        onChange={(e) => setBulkYear(e.target.value)}
                        disabled={selectedIds.length === 0}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Flags</InputLabel>
                        <Select
                          multiple
                          value={bulkFlags}
                          onChange={(e) => setBulkFlags(e.target.value as string[])}
                          input={<OutlinedInput label="Set Flags" />}
                          renderValue={(selected) =>
                            selected
                              .map((id) => mmcDropdowns.flags.find((f) => f.id === id)?.label || id)
                              .join(', ')
                          }
                        >
                          {mmcDropdowns.flags.map((flag) => (
                            <MenuItem key={flag.id} value={flag.id}>
                              <Checkbox checked={bulkFlags.includes(flag.id)} />
                              <ListItemText primary={flag.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <ColorGrid
                        colors={mmcDropdowns.colors}
                        selectedColors={bulkColors}
                        onChange={setBulkColors}
                        maxColors={3}
                        disabled={selectedIds.length === 0}
                        label="Set Colors"
                        size="small"
                      />
                    </Grid>
                  </>
                )}

                {task.type === 'plate' && plateDropdowns && (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Set Tag"
                        value={bulkTag}
                        onChange={(e) => setBulkTag(e.target.value.toUpperCase())}
                        disabled={selectedIds.length === 0}
                        inputProps={{ style: { fontFamily: 'monospace', fontWeight: 'bold' } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Region</InputLabel>
                        <Select
                          value={bulkRegion}
                          onChange={(e) => setBulkRegion(e.target.value)}
                          label="Set Region"
                        >
                          <MenuItem value=""><em>-- No change --</em></MenuItem>
                          {plateDropdowns.regions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {getCountryFlag(region.code)} {region.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Dataset</InputLabel>
                        <Select
                          value={bulkDataset}
                          onChange={(e) => setBulkDataset(e.target.value)}
                          label="Set Dataset"
                        >
                          <MenuItem value=""><em>-- No change --</em></MenuItem>
                          {plateDropdowns.datasets.map((dataset) => (
                            <MenuItem key={dataset.id} value={dataset.id}>{dataset.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Flags</InputLabel>
                        <Select
                          multiple
                          value={bulkPlateFlags}
                          onChange={(e) => setBulkPlateFlags(e.target.value as string[])}
                          input={<OutlinedInput label="Set Flags" />}
                          renderValue={(selected) =>
                            selected
                              .map((id) => plateDropdowns.flags.find((f) => f.id === id)?.label || id)
                              .join(', ')
                          }
                        >
                          {plateDropdowns.flags.map((flag) => (
                            <MenuItem key={flag.id} value={flag.id}>{flag.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {task.type === 'detection' && detectionDropdowns && (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Dataset</InputLabel>
                        <Select
                          value={bulkDetDataset}
                          onChange={(e) => setBulkDetDataset(e.target.value)}
                          label="Set Dataset"
                        >
                          <MenuItem value=""><em>-- No change --</em></MenuItem>
                          {detectionDropdowns.datasets.map((dataset) => (
                            <MenuItem key={dataset.id} value={dataset.id}>{dataset.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormControl fullWidth size="small" disabled={selectedIds.length === 0}>
                        <InputLabel>Set Flags</InputLabel>
                        <Select
                          multiple
                          value={bulkDetFlags}
                          onChange={(e) => setBulkDetFlags(e.target.value as string[])}
                          input={<OutlinedInput label="Set Flags" />}
                          renderValue={(selected) =>
                            selected
                              .map((id) => detectionDropdowns.flags.find((f) => f.id === id)?.label || id)
                              .join(', ')
                          }
                        >
                          {detectionDropdowns.flags.map((flag) => (
                            <MenuItem key={flag.id} value={flag.id}>{flag.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid size={{ xs: 12, md: 'auto' }}>
                  <Button
                    variant="contained"
                    onClick={handleBulkApplyFields}
                    disabled={selectedIds.length === 0 || bulkUpdating}
                    sx={{ bgcolor: config?.color, '&:hover': { bgcolor: config?.color, filter: 'brightness(0.9)' } }}
                  >
                    {bulkUpdating ? 'Applying...' : 'Apply to Selected'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Pagination */}
          <Box sx={{ mb: 2 }}>
            <PaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={filteredSampleIds.length}
              disabled={loadingSamples}
              onPrevious={handlePreviousPage}
              onNext={handleNextPage}
              onPageChange={handlePageChange}
            />
          </Box>

          {/* Loading indicator */}
          {loadingSamples && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Sample Grid */}
          {!loadingSamples && visibleSamples.length > 0 && (
            <Grid container spacing={2}>
              {visibleSamples.map(renderSampleCard)}
            </Grid>
          )}

          {/* Empty state */}
          {!loadingSamples && displayedSamples.length === 0 && task.samples.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No samples in this task.</Typography>
            </Paper>
          )}

          {/* Bottom Pagination */}
          {displayedSamples.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <PaginationBar
                currentPage={currentPage}
                totalPages={totalPages}
                disabled={loadingSamples}
                onPrevious={handlePreviousPage}
                onNext={handleNextPage}
                onPageChange={handlePageChange}
              />
            </Box>
          )}
        </Container>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* MMC Edit Dialog */}
      {mmcDropdowns && (
        <EditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          editField={editField}
          editValue={editValue}
          setEditValue={setEditValue}
          editModelValue={editModelValue}
          setEditModelValue={setEditModelValue}
          onSave={handleEditDialogSave}
          saving={editSaving}
          isIndividual={true}
          selectedCount={1}
          makes={mmcDropdowns.makes}
          colors={mmcDropdowns.colors}
          bodies={mmcDropdowns.bodies}
          types={mmcDropdowns.types}
          flags={mmcDropdowns.flags}
          datasets={mmcDropdowns.datasets}
          availableModels={
            (editValue && mmcDropdowns.makes.find(m => m.id === editValue)?.models) || []
          }
        />
      )}

      {/* Plate Alignment Editor */}
      {editingPlate && (
        <PlateAlignmentEditor
          open={alignmentEditorOpen}
          plateId={editingPlate.id}
          imageUrl={`${IMAGE_BASE_URLS.plate}/${editingPlate.id}`}
          initialCorners={editingPlate.corners}
          onClose={() => {
            setAlignmentEditorOpen(false);
            setEditingPlate(null);
          }}
          onCornersUpdate={handleCornersUpdate}
        />
      )}

      {/* Detection Object Editor */}
      {editingDetection && (
        <DetectionObjectEditor
          open={detectionEditorOpen}
          detection={editingDetection}
          imageUrl={`${IMAGE_BASE_URLS.detection}/${editingDetection.id}`}
          onClose={() => {
            setDetectionEditorOpen(false);
            setEditingDetection(null);
          }}
          onObjectsUpdated={handleDetectionObjectsUpdated}
          onFinish={() => {
            if (editingDetection) {
              handleMarkCompleted(editingDetection.id);
            }
          }}
        />
      )}

      {/* Finish Task Confirmation Dialog */}
      <ConfirmDialog
        open={finishConfirmOpen}
        onClose={() => setFinishConfirmOpen(false)}
        onConfirm={handleMarkFinished}
        title="Mark Task as Finished?"
        message="Once marked as finished, only an admin can reopen this task. Are you sure you want to continue?"
        confirmLabel="Mark Finished"
        loading={togglingFinished}
        icon={<CheckCircleIcon />}
        confirmColor="success"
      />

    </Box>
  );
}

export default TaskPage;
